import {
  forwardRef,
  type ComponentPropsWithRef,
  type ComponentPropsWithoutRef,
  type ElementType,
  type ReactElement,
  type ReactNode,
} from "react";
import { useAtomValue } from "jotai";
import { themeAtom } from "@src/jotai/theme";
import { twMerge } from "tailwind-merge";

type ThemeAwareClassNameProps<T extends ElementType = "div"> = {
  as?: T;
  className?: string;
  lightClassName?: string;
  darkClassName?: string;
  children?: ReactNode;
} & Omit<ComponentPropsWithoutRef<T>, "as" | "className" | "children">;

type ThemeAwareClassNameRef<T extends ElementType> = ComponentPropsWithRef<T>["ref"];

type ThemeAwareClassNameComponent = <T extends ElementType = "div">(
  props: ThemeAwareClassNameProps<T> & { ref?: ThemeAwareClassNameRef<T> },
) => ReactElement | null;

const ThemeAwareClassNameImpl = <T extends ElementType = "div">(
  { as, className, lightClassName, darkClassName, children, ...restProps }: ThemeAwareClassNameProps<T>,
  ref: ThemeAwareClassNameRef<T>,
) => {
  const theme = useAtomValue(themeAtom);
  const isDark = theme === "dark";
  const Component = as ?? "div";

  return (
    <Component ref={ref} className={twMerge(className, isDark ? darkClassName : lightClassName)} {...restProps}>
      {children}
    </Component>
  );
};

export const ThemeAwareClassName = forwardRef(
  ThemeAwareClassNameImpl as unknown as (
    props: ThemeAwareClassNameProps<ElementType>,
    ref: ThemeAwareClassNameRef<ElementType>,
  ) => ReactElement | null,
) as ThemeAwareClassNameComponent;
